<template lang="pug">
  .hcb-gmap(v-if="locationsLoaded")
    GmapMap.map(ref="map" :center="center" :zoom="zoom" :options="{ disableDefaultUI: true, zoomControl: true, fullscreenControl: true, minZoom: 3, maxZoom: 14 }" :style="mapDimensions")
      GmapInfoWindow(v-if="selectedMarker" :opened="!!selectedMarker" :options="selectedMarker.options" :position="selectedMarker.position" @closeclick="selectedMarker = null;" :title="selectedMarker.location.name")
        MarkerInfoWindow(:location="selectedMarker.location")

      GmapMarker.brand-color-1(
        v-for="marker in markers"
        :key="marker.location.code"
        :position="marker.position"
        @click="() => onMarkerClicked(marker)"
        :icon="iconConfig"
        :title="marker.location.name"
      )

      GmapMarker(v-if="currentPlace" :position="currentPlace.position" :title="currentPlace.name")

</template>

<script>
  /* globals MR */
  import { components, helpers, getGoogleMapsAPI } from 'gmap-vue';
  const { googleMapsApiInitializer } = helpers;

  import MarkerInfoWindow from '@components/ColorBarMapSection/MarkerInfoWindow';

  import { mapState, mapMutations, mapActions } from 'vuex';

  import throttle from 'lodash.throttle';

  const UNITED_STATES_CENTER = {
    lat: 39.8097343,
    lng: -98.5556199,
  };
  const DEFAULT_ZOOM_LEVEL = 3;
  const MAX_MARKERS_DISTANCE_FROM_CENTER_IN_MILES = 60;
  const KMS_TO_MILES_COEFFICIENT = 0.000621371 * 1000;

  export default {
    name: "HcbGmap",

    components: {
      GmapMarker: components.Marker,
      GmapMap: components.MapLayer,
      GmapAutocomplete: components.Autocomplete,
      GmapInfoWindow: components.InfoWindow,
      MarkerInfoWindow
    },

    data() {
      return {
        center: UNITED_STATES_CENTER,
        zoom: DEFAULT_ZOOM_LEVEL,
        locationsLoaded: false,
        startMapLocation: null,
        selectedMarker: null,
        currentPlace: null
      };
    },

    props: {
      startLocation: {
        type: Object,
        default: null
      },

      nearbyLocationsMaxDistance: {
        type: Number,
        default: MAX_MARKERS_DISTANCE_FROM_CENTER_IN_MILES,
      }
    },

    computed: {
      ...mapState('global', ['windowData']),
      ...mapState('colorbar', ['mapLocations', 'nearbyMapLocations', 'selectedMapLocation']),

      gmapApi: getGoogleMapsAPI,

      isMobile() {
        return this.windowData.breakpoint === 'mobile';
      },

      mapDimensions() {
        if (!this.isMobile) {
          return {
            width: '100%',
            height: '450px',
          };
        }

        return {
          width: '100%',
          height: '370px'
        };
      },

      markers() {
        if (!this.mapLocations) {
          return [];
        }

        return this.mapLocations.map(location => {
          return {
            location,
            position: location.position,
            options: {
              maxWidth: 384,
              pixelOffset: {
                width: 0,
                height: -20,
              },
            },
          };
        });
      },

      iconConfig() {
        const width = 20;
        return {
          url: `https://d3ewrnwdcmri66.cloudfront.net/content/images/2022/2/l1cm1wy8-map-marker-2/map-marker-2.png`,
          scaledSize: {
            width,
            height: Math.round(width * 1.25),
          },
        };
      },
    },

    methods: {
      ...mapActions('colorbar', ['getActiveLocationsListForMapView']),
      ...mapMutations('colorbar', ['setNearbyMapLocations', 'setSelectedMapLocation']),

      onMarkerClicked(marker) {
        this.trackEvent(`Map Marked Clicked`, { marker });
        this.selectedMarker = marker;
        if (marker.location) {
          this.setSelectedMapLocation(marker.location);
        }
      },

      setNearbyLocations(position) {
        let nearbyLocations = [];

        this.mapLocations.forEach(location => {
          let distanceInMeters = this.gmapApi.maps.geometry.spherical.computeDistanceBetween(position,location.position);
          let distanceInMiles = (distanceInMeters / 1000) * KMS_TO_MILES_COEFFICIENT;

          if (distanceInMiles > this.nearbyLocationsMaxDistance) {
            return;
          }

          nearbyLocations.push({
            ...location,
            distance: distanceInMiles,
          });
        });

        this.setNearbyMapLocations(nearbyLocations);
      },

      selectLocation(location) {
        this.selectedMarker = this.markers.find(marker => marker.location.code == location.code);
      },

      resizeMap: throttle(function () {
        this.gmapApi?.maps?.event?.trigger(this.$refs.map, 'resize');
      }, 300),
    },

    watch: {
      mapDimensions() {
        this.resizeMap();
      },

      selectedMapLocation() {
        this.selectLocation(this.selectedMapLocation);
      }
    },

    async mounted() {
      googleMapsApiInitializer({
        key: MR.config.googleAutocomplete,
        libraries: 'places,geometry',
        v: '2',
      }, false);

      await this.$gmapApiPromiseLazy();

      await this.getActiveLocationsListForMapView().then(() => {
        if (this.startLocation) {
          this.startMapLocation = this.mapLocations.find(location => location.code == this.startLocation.code);

          if (this.startMapLocation) {
            this.setNearbyLocations(this.startMapLocation.position);
            this.setSelectedMapLocation(this.startMapLocation);

            if (this.nearbyMapLocations) {
              this.$nextTick(() => {
                this.selectedMarker = this.markers.find(marker => marker.location.code == this.startMapLocation.code);

                let bounds = new this.gmapApi.maps.LatLngBounds();
                this.nearbyMapLocations.forEach(({ position }) => {
                  bounds.extend(position);
                });
                bounds.extend(this.startMapLocation.position);

                this.$nextTick(() => {
                  this.$refs.map.fitBounds(bounds);
                });
              });
            }
          }
        }

        this.locationsLoaded = true;
      });
    },
  };
</script>

<style scoped lang="stylus">
</style>
